import { useCreate, useNotification } from "@refinedev/core";
import { Button, Drawer, Form, Input, Select } from "antd";
import type { FC } from "react";
import type { Project } from "../../../types";

interface AddProjectDrawerProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

export const AddProjectDrawer: FC<AddProjectDrawerProps> = ({ open, onClose, isMobile }) => {
  const [form] = Form.useForm();
  const { open: notify } = useNotification();
  const { mutate: createProject, mutation: { isPending: creating } } = useCreate<Project>();

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const handleSubmit = (values: { name: string; kind: "site" | "library" }) => {
    createProject(
      { resource: "projects", values: { name: values.name, kind: values.kind } },
      {
        onSuccess: handleClose,
        onError: (error) => {
          if (error.statusCode === 409) {
            notify?.({ type: "error", message: "A project with this name already exists" });
          } else {
            notify?.({ type: "error", message: "Failed to create project" });
          }
        },
      }
    );
  };

  return (
    <Drawer title="Add project" open={open} onClose={handleClose} width={isMobile ? "100%" : 480}>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="name"
          label="Project name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="e.g. My Client Project" />
        </Form.Item>
        <Form.Item
          name="kind"
          label="Type"
          initialValue="site"
          rules={[{ required: true }]}
          extra="A library has no site of its own and can deploy its snippets to any project; a site can only deploy to its own environments."
        >
          <Select
            options={[
              { value: "site", label: "Site" },
              { value: "library", label: "Library" },
            ]}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={creating} block>
          Add project
        </Button>
      </Form>
    </Drawer>
  );
};
